// SPDX-License-Identifier: AGPL-3.0-or-later

import type { ReactNode } from 'react';

export interface EmptyStateProps {
  /** Decorative glyph rendered above the title. Aria-hidden by default. */
  icon?: ReactNode;
  title: string;
  body?: ReactNode;
  /** Primary action — usually a Link or button. Stays prominent below the body. */
  action?: ReactNode;
  /** Optional secondary action shown next to the primary action. */
  secondaryAction?: ReactNode;
  /** Test attribute so e2e specs can find a specific empty state. */
  dataTestId?: string;
}

/**
 * Standard "nothing here yet" placeholder for list pages. Replaces
 * the bare "No X" copy with an icon + explanatory body + a clear
 * primary action — the criterion in the bead is that every list page
 * has an empty state with a real CTA, not a dead end.
 */
export function EmptyState({
  icon,
  title,
  body,
  action,
  secondaryAction,
  dataTestId,
}: EmptyStateProps) {
  return (
    <div className="empty-state" data-empty-state={dataTestId ?? true}>
      {icon !== undefined ? (
        <span className="empty-state-icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <p className="empty-state-title">{title}</p>
      {body !== undefined ? <p className="empty-state-body">{body}</p> : null}
      {action !== undefined || secondaryAction !== undefined ? (
        <div className="empty-state-actions">
          {action}
          {secondaryAction}
        </div>
      ) : null}
    </div>
  );
}
